import React from 'react';
import { View, StyleSheet, Text, Image } from '@react-pdf/renderer';
import { toPairs } from 'lodash';
import { range2Value, value2Range, getLabels } from '../../utilities/color';

/**
 * Interface for plot properties
 */
interface PlotProps {
  label: string;
  units: string;
  minLevel: number;
  maxLevel: number;
  [key: string]: any; // For any additional properties that might be used
}

/**
 * Interface for gradient mapping
 */
interface Gradient {
  [key: number]: string; // Maps numeric values to color strings
}

/**
 * Interface for bounding box
 */
interface BoundingBox {
  width: number;
  height: number;
}

/**
 * Interface for MuiScale component props
 */
interface MuiScaleProps {
  plotProps: PlotProps;
  gradient: Gradient;
  bbox: BoundingBox;
}

/**
 * MUI Scale section component that replaces the styled-components Scale section
 * 
 * This component provides a Material UI equivalent of the original Scale section component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiScaleProps} props - Component props
 * @param {PlotProps} props.plotProps - Plot configuration properties
 * @param {string} props.plotProps.label - Scale label text
 * @param {string} props.plotProps.units - Units label text  
 * @param {number} props.plotProps.minLevel - Minimum scale value
 * @param {number} props.plotProps.maxLevel - Maximum scale value
 * @param {Gradient} props.gradient - Color gradient mapping for scale values
 * @returns {JSX.Element} MUI Scale section component
 * 
 * @description
 * Generates scale visualization by:
 * 1. Creating canvas element for gradient:
 *    - Draws vertical gradient lines
 *    - Maps values to colors using gradient
 *    - Adds value labels at intervals
 * 2. Converting canvas to PNG image
 * 3. Rendering in PDF with:
 *    - Label text
 *    - Gradient image
 *    - Units text
 */
const MuiScale: React.FC<MuiScaleProps> = (props) => {
  const { plotProps, gradient, bbox } = props;
  const { label, units } = plotProps;
  const scaleWidth = bbox.width;
  const image = getImage(plotProps, gradient, scaleWidth);

  // Check if units are already included in the label (e.g., "Distance from camera (Feet)")
  const unitsInLabel = label && units && label.includes(`(${units})`);

  // Define styles for PDF scale
  const styles = StyleSheet.create({
    container: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: '20',
      marginBottom: '20',
      width: scaleWidth,
    },
    label: {
      width: 'auto',
      marginBottom: '4',
      fontSize: '11',
      fontWeight: 500,
    },
    scaleRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    image: {
      height: '25',
      width: scaleWidth,
      borderRadius: '2'
    },
    units: {
      width: 'auto',
      marginLeft: '4',
      fontSize: '10'
    }
  });

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.scaleRow}>
        <Image style={styles.image} src={image} />
        {!unitsInLabel && <Text style={styles.units}>{units}</Text>}
      </View>
    </View>
  );
};

/**
 * Helper function that generates gradient scale image
 *
 * @param {PlotProps} plotProps - Plot configuration
 * @param {Gradient} gradient - Color gradient mapping (actually RGBAColor arrays)
 * @param {number} canvasWidth - Width of the canvas to match plot width
 * @returns {string} PNG data URL of generated scale image
 */
const getImage = (plotProps: PlotProps, gradient: any, canvasWidth: number): string => {
  const height = 25;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  // Set dimensions
  canvas.width = canvasWidth;
  canvas.height = height;

  // Ensure context is not null before using it
  if (context) {
    // Fill it with gradient
    context.clearRect(0, 0, canvasWidth, height);
    for (let x = 0; x <= canvasWidth; x++) {
      const colorIndex = range2Value(plotProps, canvasWidth, x);
      const rgbaColor = gradient[Math.round(colorIndex * (plotProps.accuracy || 100))];

      // Convert array to rgba string, handling the opacity correctly
      const rgbaString = typeof rgbaColor === 'object' && Array.isArray(rgbaColor) && rgbaColor.length >= 3
        ? `rgba(${rgbaColor.join(',')})`
        : 'rgba(0,0,0,0)';

      context.strokeStyle = rgbaString;
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }

    // Add labels
    context.fillStyle = "rgba(45, 47, 56, 0.8)";
    context.font = "normal 9px arial";
    context.textAlign = "center";
    context.textBaseline = "middle";
    const labels = getLabels(plotProps);
    toPairs(labels).forEach(([key, value]) =>
      context.fillText(key, value2Range(plotProps, canvasWidth, value), height / 2));
  }

  return canvas.toDataURL("image/png");
};

export default MuiScale;
